// React.
import { useContext, useMemo, useState } from 'react';

// Types.
import { BlockingSessionTime } from '../../../types/instance/BlockingSession';

// Components.
import HighchartsWithData from '../../../component/HighchartsWithData';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

// Helpers.
import { getConvertedTimeToUTC, highchartsCredits } from '../../../helpers/utils';
import { buildApiQuery } from '../../../helpers/timeRangeUtils';
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";

// Context
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { useLicences } from "../../../context/LicenceContext";
import { CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE } from "../../Constants";
import Helper from "../../../helpers/Helper";
import BlockingSessionApi from '../../../api/instance/BlockingSessionApi';

const CHART_MODE = {
    BLOCKING_TIME: 0,
    BLOCKED_SESSIONS: 1,
};

const chartModeOptions = [
    { label: 'Blocking Time', value: CHART_MODE.BLOCKING_TIME },
    { label: 'Blocked Sessions', value: CHART_MODE.BLOCKED_SESSIONS },
];

export default function BlockingSessionsChart(props: {
    error: string | null,
    loading: boolean,
    data: BlockingSessionTime[],
    blockingList: any[],
    instanceId: number,
}) {
    const { error, loading, data, blockingList, instanceId } = props;
    const { licences } = useLicences();
    const timeRangeContext = useContext(TimeRangeContext);
    const [chartMode, setChartMode] = useState<number>(CHART_MODE.BLOCKING_TIME);
    const [sessionSeries, setSessionSeries] = useState<any[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);

    const blockedSessions = useMemo(() => {
        const sessions: any[] = [];
        blockingList.forEach(blocker => {
            if (blocker.subRows?.length) {
                blocker.subRows.forEach((blocked: any) => {
                    if (blocked.session && !sessions.find(s => s.session === blocked.session)) {
                        sessions.push(blocked);
                    }
                });
            }
        });
        return sessions.slice(0, 20);
    }, [blockingList]);

    void useMemo(async () => {
        if (chartMode !== CHART_MODE.BLOCKED_SESSIONS || blockedSessions.length === 0) {
            setSessionSeries([]);
            return;
        }
        setSessionsLoading(true);

        const promises = blockedSessions.map(session =>
            BlockingSessionApi.fetchSessionTime(buildApiQuery(timeRangeContext.timeRange), instanceId, session.session)
                .then((res: Response) => res.json())
                .catch(() => [])
        );

        const results = await Promise.all(promises);

        const series = results.map((timeData: any[], index) => ({
            name: blockedSessions[index].session,
            color: CHART_COLOURS_STATEMENTS[index],
            type: 'column' as const,
            data: Array.isArray(timeData)
                ? timeData.map((item: BlockingSessionTime) => [getConvertedTimeToUTC(item), item.waittime])
                : [],
            zIndex: index,
        }));

        setSessionSeries(series);
        setSessionsLoading(false);
    }, [chartMode, blockedSessions, timeRangeContext]);

    const blockingTimeOptions = useMemo(() => {
        return chartOptionsGranularity([
            {
                name: 'Blocking Time',
                data: data.map(item => [getConvertedTimeToUTC(item), item['waittime']])
            }
        ], null, {
            credits: { enabled: highchartsCredits(licences) },
            timeRangeContext: timeRangeContext,
            tooltip: {
                useHTML: true,
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
            legend: true,
            plotLines: [],
            plotBands: [],
            yAxis: [{
                title: { text: 'Duration' },
                labels: {
                    formatter: function (): string {
                        // @ts-ignore
                        return Helper.getTimeInEnglish(this?.value);
                    }
                },
                min: 0,
            }]
        });
    }, [data, licences, timeRangeContext]);

    const blockedSessionsOptions = useMemo(() => {
        if (sessionSeries.length === 0) return null;
        return chartOptionsGranularity(sessionSeries, null, {
            credits: { enabled: highchartsCredits(licences) },
            timeRangeContext: timeRangeContext,
            tooltip: {
                useHTML: true,
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
            legend: true,
            plotLines: [],
            yAxis: [{
                title: { text: 'Blocked Time' },
                labels: {
                    formatter: function (): string {
                        // @ts-ignore
                        return Helper.getTimeInEnglish(this?.value);
                    }
                },
                min: 0,
            }]
        });
    }, [sessionSeries, licences, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    const selectedLabel = chartModeOptions.find(o => o.value === chartMode)?.label ?? 'Blocking Time';
    const isLoadingChart = loading || (chartMode === CHART_MODE.BLOCKED_SESSIONS && sessionsLoading);

    return (
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                Blocking time
                <div className="float-end d-inline-flex align-items-center gap-1">
                    <div className="dropdown">
                        <button id="btnBlockingChartMode" type="button"
                                className="btn btn-xsm btn-primary dropdown-toggle"
                                data-bs-toggle="dropdown" aria-expanded="false">
                            {selectedLabel}
                        </button>
                        <ul className="dropdown-menu border" aria-labelledby="btnBlockingChartMode">
                            <li>
                                {chartModeOptions.map(({ label, value }) => (
                                    <span
                                        key={value}
                                        className={`dropdown-item ${chartMode === value ? 'active' : ''}`}
                                        role="button"
                                        onClick={() => setChartMode(value)}
                                    >
                                        {label}
                                    </span>
                                ))}
                            </li>
                        </ul>
                    </div>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapse-space" aria-expanded="false"
                       aria-controls="collapse-space"/>
                </div>
            </div>
            <div id="collapse-space" className="card-body collapse show">
                {chartMode === CHART_MODE.BLOCKING_TIME && (
                    <HighchartsWithData error={error} loading={isLoadingChart}
                                        length={data.length} options={blockingTimeOptions}/>
                )}
                {chartMode === CHART_MODE.BLOCKED_SESSIONS && (
                    isLoadingChart
                        ? <HighchartsWithData error={null} loading={true} length={0} options={blockedSessionsOptions ?? blockingTimeOptions}/>
                        : blockedSessionsOptions
                            ? <HighchartsReact constructorType={"chart"} highcharts={Highcharts} options={blockedSessionsOptions}/>
                            : <HighchartsWithData error={error} loading={false} length={0} options={blockingTimeOptions}/>
                )}
            </div>
        </div>
    );
}
